import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "../../api.js";
import { useAuth } from "../../context/AuthContext.jsx";
import { ForumProvider, useForum } from "./ForumContext.jsx";
import "./Forum.css";

// ── Collection config ────────────────────────────────────────────────────────
const COLLECTIONS = {
  bookshelf: {
    title_ar: "رف الكتب الفنية", title_en: "Artists' Bookshelf",
    subtitle: "كتب وPDF ومجلات فنية", icon: "📚", breadcrumb: "رف الكتب",
    types: ["book", "audiobook", "pdf", "magazine"],
    submitLabel: "+ اقتراح كتاب", submitTitle: "اقتراح كتاب أو مرجع",
  },
  audio: {
    title_ar: "المكتبة الصوتية", title_en: "Audio Library",
    subtitle: "تسجيلات ومحاضرات وبودكاست فني", icon: "🎵", breadcrumb: "المكتبة الصوتية",
    types: ["audio", "audiobook", "podcast", "music", "video"],
    submitLabel: "+ إضافة تسجيل", submitTitle: "إضافة ملف صوتي أو مرئي",
  },
  documents: {
    title_ar: "الوثائق المختارة", title_en: "Selected Documents",
    subtitle: "وثائق وتقارير وأبحاث فنية", icon: "📋", breadcrumb: "الوثائق المختارة",
    types: ["pdf", "doc", "report", "research"],
    submitLabel: "+ إضافة وثيقة", submitTitle: "إضافة وثيقة أو تقرير",
  },
  archive: {
    title_ar: "الأرشيف الفني", title_en: "Art Archive",
    subtitle: "أعمال وصور وملفات فنية أرشيفية", icon: "🗃️", breadcrumb: "الأرشيف الفني",
    types: ["image", "artwork", "video", "pdf"],
    submitLabel: "+ إضافة للأرشيف", submitTitle: "إضافة ملف للأرشيف الفني",
  },
};

const TYPE_ICONS = {
  book:"📚", audiobook:"🎧", pdf:"📄", magazine:"📰",
  audio:"🔊", podcast:"🎙️", music:"🎼", video:"🎥",
  image:"🖼️", artwork:"🎨", doc:"📝", report:"📊", research:"🔬",
};
const TYPE_LABELS = {
  book:"كتاب", audiobook:"كتاب صوتي", pdf:"PDF", magazine:"مجلة",
  audio:"صوت", podcast:"بودكاست", music:"موسيقى", video:"فيديو",
  image:"صورة", artwork:"عمل فني", doc:"وثيقة", report:"تقرير", research:"بحث",
};
const VIEW_LABEL = {
  book:"📖 قراءة", audiobook:"🎧 استماع", pdf:"📄 قراءة", magazine:"📰 قراءة",
  audio:"▶ استماع", podcast:"▶ استماع", music:"▶ استماع", video:"▶ مشاهدة",
  image:"🖼️ عرض", artwork:"🖼️ عرض", doc:"📄 قراءة", report:"📊 قراءة", research:"🔬 قراءة",
};
const ACCEPT = ".pdf,.epub,.doc,.docx,.mp3,.mp4,.wav,.ogg,.m4a,.jpg,.jpeg,.png,.webp";

// ── Read directory recursively ───────────────────────────────────────────────
async function readDirectoryFiles(dirEntry) {
  return new Promise((resolve) => {
    const reader = dirEntry.createReader();
    const all = [];
    const readBatch = () => {
      reader.readEntries(async (entries) => {
        if (!entries.length) return resolve(all);
        for (const entry of entries) {
          if (entry.isFile) {
            await new Promise(r => entry.file(f => { all.push(f); r(); }, () => r()));
          } else if (entry.isDirectory) {
            const sub = await readDirectoryFiles(entry);
            all.push(...sub);
          }
        }
        readBatch();
      }, () => resolve(all));
    };
    readBatch();
  });
}

// Mirror the server allowlist — gives instant client-side feedback before upload
const CLIENT_LIMITS = {
  ".jpg": 10, ".jpeg": 10, ".png": 10, ".webp": 10,
  ".pdf": 50, ".epub": 30, ".doc": 20, ".docx": 20,
  ".mp3": 100, ".wav": 100, ".ogg": 100, ".m4a": 100,
  ".mp4": 200,
};

function clientValidate(file) {
  const ext = file.name.toLowerCase().match(/\.[^.]+$/)?.[0];
  if (!ext || !(ext in CLIENT_LIMITS))
    return `نوع الملف "${ext || ""}" غير مسموح به`;
  const maxMB = CLIENT_LIMITS[ext];
  if (file.size > maxMB * 1024 * 1024)
    return `الملف كبير جداً: ${(file.size/1024/1024).toFixed(1)} MB — الحد ${maxMB} MB`;
  return null;
}

function guessType(file) {
  const n = file.name.toLowerCase();
  if (n.endsWith(".mp4") || n.endsWith(".mov")) return "video";
  if (n.endsWith(".mp3") || n.endsWith(".wav") || n.endsWith(".ogg") || n.endsWith(".m4a")) return "audio";
  if (n.endsWith(".pdf")) return "pdf";
  if (n.endsWith(".epub")) return "audiobook";
  if (n.endsWith(".jpg") || n.endsWith(".jpeg") || n.endsWith(".png") || n.endsWith(".webp")) return "image";
  if (n.endsWith(".doc") || n.endsWith(".docx")) return "doc";
  return "book";
}

function downloadFile(url, filename) {
  fetch(url)
    .then(r => r.blob())
    .then(blob => {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename || "download";
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(a.href);
    })
    .catch(() => window.open(url, "_blank"));
}

let _uid = 0;
const uid = () => ++_uid;

// ── Upload queue ─────────────────────────────────────────────────────────────
const ALBUM_TYPES = ["music", "audiobook", "podcast", "audio"];

function UploadQueue({ queue, setQueue, token, collection, defaultTypes }) {
  const update = (id, patch) => setQueue(q => q.map(i => i.id === id ? { ...i, ...patch } : i));
  const remove = (id) => setQueue(q => q.filter(i => i.id !== id));

  const uploadOne = useCallback(async (item) => {
    if (!item.title_en.trim()) return;
    update(item.id, { status: "uploading" });
    try {
      const fd = new FormData();
      fd.append("file", item.file);
      const { data: up } = await axios.post(`${API_BASE}/api/upload`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await axios.post(`${API_BASE}/api/forum/bookshelf`, {
        title_en: item.title_en, title_ar: item.title_ar,
        author_en: item.author_en, author_ar: item.author_ar,
        description_ar: item.description_ar, type: item.type,
        category: item.category || undefined,
        link_url: item.link_url || undefined,
        coverUrl: item.coverUrl || undefined,
        collection, fileUrl: up.url,
      }, { headers: { Authorization: `Bearer ${token}` } });
      update(item.id, { status: "done" });
    } catch (err) {
      update(item.id, { status: "error", errMsg: err.response?.data?.error || "خطأ في الرفع" });
    }
  }, [token, collection]);

  const uploadAlbum = useCallback(async (item) => {
    if (!item.title_en.trim()) return;
    const total = item.files.length;
    update(item.id, { status: "uploading", uploadProgress: `0 / ${total}` });
    try {
      const tracks = [];
      for (const [i, t] of item.files.entries()) {
        const fd = new FormData();
        fd.append("file", t.file);
        const { data: up } = await axios.post(`${API_BASE}/api/upload`, fd, {
          headers: { Authorization: `Bearer ${token}` },
        });
        tracks.push({ name: t.name, url: up.url, order: t.order });
        update(item.id, { uploadProgress: `${i + 1} / ${total}` });
      }
      await axios.post(`${API_BASE}/api/forum/bookshelf`, {
        title_en: item.title_en.trim(), title_ar: item.title_ar || "",
        author_ar: item.author_ar || "", description_ar: item.description_ar || "",
        type: item.type || "music", category: item.category || undefined,
        collection, tracks,
      }, { headers: { Authorization: `Bearer ${token}` } });
      update(item.id, { status: "done", uploadProgress: null });
    } catch (err) {
      update(item.id, { status: "error", errMsg: err.response?.data?.error || err.message || "خطأ في الرفع", uploadProgress: null });
    }
  }, [token, collection]);

  const submitAll = () => queue.filter(i => i.status === "pending" && i.title_en.trim()).forEach(i => i.isAlbum ? uploadAlbum(i) : uploadOne(i));
  const allDone  = queue.length > 0 && queue.every(i => i.status === "done");
  const anyReady = queue.some(i => i.status === "pending" && i.title_en.trim());

  const CATEGORY_OPTS = [
    ["","— بدون تصنيف —"],["تاريخ الفن","تاريخ الفن · Art History"],["أدب","أدب · Literature"],
    ["روايات","روايات · Novels"],["كتب أكاديمية","كتب أكاديمية · Academic Books"],
    ["أوراق بحثية","أوراق بحثية · Research Papers"],["نقد فني","نقد فني · Artistic Critique"],
    ["كتب فنون","كتب فنون · Art Books"],["كتب الفنانين","كتب الفنانين · Artist Books"],
    ["أرشيف الفنانين","أرشيف الفنانين · Artists Archive"],["موسيقى","موسيقى · Music"],
    ["سينما وفيديو","سينما وفيديو · Film & Video"],["وثائق رسمية","وثائق رسمية · Official Documents"],
    ["أخرى","أخرى · Other"],
  ];

  return (
    <div style={{ marginTop: 16 }}>
      {queue.map(item => (
        <div key={item.id} style={{
          border: "1px solid var(--border)", borderRadius: "var(--r-xs)", padding: "14px 16px", marginBottom: 10,
          background: item.status === "done" ? "rgba(30,86,49,0.06)" : "var(--bg-2)",
          opacity: item.status === "done" ? 0.7 : 1,
        }}>
          {/* ── Album / multi-track item ── */}
          {item.isAlbum ? (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <span style={{ fontSize: "1.4rem" }}>🎵</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: "0.85rem", fontWeight: 700 }}>
                    {item.folderName || "ألبوم صوتي"} &nbsp;·&nbsp;
                    <span style={{ fontWeight: 400, color: "var(--text-3)" }}>{item.files.length} {item.files.length === 1 ? "مقطع" : "مقاطع"}</span>
                  </div>
                  <div style={{ fontSize: "0.7rem", color: "var(--text-3)" }}>
                    {(item.files.reduce((s, t) => s + t.file.size, 0) / 1024 / 1024).toFixed(1)} MB إجمالاً
                  </div>
                </div>
                {item.status === "done"      && <span style={{ color: "var(--green)", fontWeight: 700 }}>✓ تم الرفع</span>}
                {item.status === "uploading" && <span style={{ color: "var(--text-3)", fontSize: "0.8rem" }}>يُرفع {item.uploadProgress}…</span>}
                {item.status === "error"     && <span style={{ color: "var(--red)", fontSize: "0.78rem" }}>{item.errMsg}</span>}
                {item.status === "pending"   && <button onClick={() => remove(item.id)} style={{ background: "none", border: "none", color: "var(--text-3)", cursor: "pointer", fontSize: "1rem" }}>✕</button>}
              </div>

              {/* Track list */}
              <div style={{ background: "var(--bg-3)", borderRadius: 6, padding: "8px 12px", marginBottom: 12, maxHeight: 180, overflowY: "auto" }}>
                {item.files.map((t, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", padding: "4px 0", borderBottom: i < item.files.length - 1 ? "1px solid var(--border)" : "none" }}>
                    <span style={{ fontSize: "0.7rem", color: "var(--text-3)", width: 20, textAlign: "center", flexShrink: 0 }}>{t.order}</span>
                    <span style={{ flex: 1, fontSize: "0.78rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.name}</span>
                    <span style={{ fontSize: "0.68rem", color: "var(--text-3)", flexShrink: 0 }}>{(t.file.size / 1024 / 1024).toFixed(1)} MB</span>
                  </div>
                ))}
              </div>

              {/* Album metadata */}
              {item.status === "pending" && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <div>
                    <label style={{ fontSize: "0.72rem", color: "var(--text-3)", display: "block", marginBottom: 3 }}>النوع</label>
                    <select className="forum-form-select" style={{ fontSize: "0.8rem", padding: "5px 8px" }}
                      value={item.type} onChange={e => update(item.id, { type: e.target.value })}>
                      {ALBUM_TYPES.map(t => <option key={t} value={t}>{TYPE_ICONS[t]} {TYPE_LABELS[t]}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: "0.72rem", color: "var(--text-3)", display: "block", marginBottom: 3 }}>الاسم / Title *</label>
                    <input className="forum-form-input" style={{ fontSize: "0.8rem", padding: "5px 8px", direction: "ltr" }}
                      placeholder="Album or audiobook title…" value={item.title_en} onChange={e => update(item.id, { title_en: e.target.value })} />
                  </div>
                  <div>
                    <label style={{ fontSize: "0.72rem", color: "var(--text-3)", display: "block", marginBottom: 3 }}>الاسم (عربي)</label>
                    <input className="forum-form-input" style={{ fontSize: "0.8rem", padding: "5px 8px" }}
                      placeholder="اسم الألبوم" value={item.title_ar} onChange={e => update(item.id, { title_ar: e.target.value })} />
                  </div>
                  <div>
                    <label style={{ fontSize: "0.72rem", color: "var(--text-3)", display: "block", marginBottom: 3 }}>الفنان / Artist</label>
                    <input className="forum-form-input" style={{ fontSize: "0.8rem", padding: "5px 8px" }}
                      placeholder="Artist / Author" value={item.author_ar} onChange={e => update(item.id, { author_ar: e.target.value })} />
                  </div>
                  <div style={{ gridColumn: "1/-1" }}>
                    <label style={{ fontSize: "0.72rem", color: "var(--text-3)", display: "block", marginBottom: 3 }}>التصنيف</label>
                    <select className="forum-form-input" style={{ fontSize: "0.8rem", padding: "5px 8px" }}
                      value={item.category || ""} onChange={e => update(item.id, { category: e.target.value })}>
                      {CATEGORY_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                </div>
              )}
            </>
          ) : (
            /* ── Single file item ── */
            <>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: item.status === "pending" ? 10 : 0 }}>
            <span style={{ fontSize: "1.3rem" }}>{TYPE_ICONS[item.type] || "📄"}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: "0.8rem", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.file.name}</div>
              <div style={{ fontSize: "0.72rem", color: "var(--text-3)" }}>{(item.file.size / 1024 / 1024).toFixed(1)} MB</div>
            </div>
            {item.status === "done"     && <span style={{ color: "var(--green)", fontWeight: 700 }}>✓ تم</span>}
            {item.status === "uploading"&& <span style={{ color: "var(--text-3)", fontSize: "0.82rem" }}>جارٍ الرفع…</span>}
            {item.status === "error"   && <span style={{ color: "var(--red)", fontSize: "0.78rem" }}>{item.errMsg}</span>}
            {item.status === "pending" && <button onClick={() => remove(item.id)} style={{ background: "none", border: "none", color: "var(--text-3)", cursor: "pointer", fontSize: "1rem" }}>✕</button>}
          </div>
          {item.status === "pending" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <div>
                <label style={{ fontSize: "0.72rem", color: "var(--text-3)", display: "block", marginBottom: 3 }}>النوع</label>
                <select className="forum-form-select" style={{ fontSize: "0.8rem", padding: "5px 8px" }}
                  value={item.type} onChange={e => update(item.id, { type: e.target.value })}>
                  {defaultTypes.map(t => <option key={t} value={t}>{TYPE_ICONS[t]} {TYPE_LABELS[t]}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: "0.72rem", color: "var(--text-3)", display: "block", marginBottom: 3 }}>Title (EN) *</label>
                <input className="forum-form-input" style={{ fontSize: "0.8rem", padding: "5px 8px", direction: "ltr" }}
                  placeholder="Title…" value={item.title_en} onChange={e => update(item.id, { title_en: e.target.value })} />
              </div>
              <div>
                <label style={{ fontSize: "0.72rem", color: "var(--text-3)", display: "block", marginBottom: 3 }}>الاسم (عربي)</label>
                <input className="forum-form-input" style={{ fontSize: "0.8rem", padding: "5px 8px" }}
                  placeholder="الاسم" value={item.title_ar} onChange={e => update(item.id, { title_ar: e.target.value })} />
              </div>
              <div>
                <label style={{ fontSize: "0.72rem", color: "var(--text-3)", display: "block", marginBottom: 3 }}>المؤلف / Author</label>
                <input className="forum-form-input" style={{ fontSize: "0.8rem", padding: "5px 8px" }}
                  placeholder="Author" value={item.author_ar} onChange={e => update(item.id, { author_ar: e.target.value })} />
              </div>
              <div>
                <label style={{ fontSize: "0.72rem", color: "var(--text-3)", display: "block", marginBottom: 3 }}>التصنيف</label>
                <select className="forum-form-input" style={{ fontSize: "0.8rem", padding: "5px 8px" }}
                  value={item.category || ""} onChange={e => update(item.id, { category: e.target.value })}>
                  <option value="">— بدون تصنيف —</option>
                  <option value="تاريخ الفن">تاريخ الفن · Art History</option>
                  <option value="أدب">أدب · Literature</option>
                  <option value="روايات">روايات · Novels</option>
                  <option value="كتب أكاديمية">كتب أكاديمية · Academic Books</option>
                  <option value="أوراق بحثية">أوراق بحثية · Research Papers</option>
                  <option value="نقد فني">نقد فني · Artistic Critique</option>
                  <option value="كتب فنون">كتب فنون · Art Books</option>
                  <option value="كتب الفنانين">كتب الفنانين · Artist Books</option>
                  <option value="أرشيف الفنانين">أرشيف الفنانين · Artists Archive</option>
                  <option value="موسيقى">موسيقى · Music</option>
                  <option value="سينما وفيديو">سينما وفيديو · Film & Video</option>
                  <option value="وثائق رسمية">وثائق رسمية · Official Documents</option>
                  <option value="أخرى">أخرى · Other</option>
                </select>
              </div>
              <div style={{ gridColumn: "1/-1" }}>
                <label style={{ fontSize: "0.72rem", color: "var(--text-3)", display: "block", marginBottom: 3 }}>وصف</label>
                <input className="forum-form-input" style={{ fontSize: "0.8rem", padding: "5px 8px" }}
                  placeholder="وصف مختصر" value={item.description_ar} onChange={e => update(item.id, { description_ar: e.target.value })} />
              </div>
              <div>
                <label style={{ fontSize: "0.72rem", color: "var(--text-3)", display: "block", marginBottom: 3 }}>🖼 رابط صورة الغلاف (اختياري)</label>
                <input className="forum-form-input" style={{ fontSize: "0.8rem", padding: "5px 8px", direction: "ltr" }}
                  placeholder="https://…cover.jpg" value={item.coverUrl || ""} onChange={e => update(item.id, { coverUrl: e.target.value })} />
              </div>
              <div>
                <label style={{ fontSize: "0.72rem", color: "var(--text-3)", display: "block", marginBottom: 3 }}>🔗 رابط المصدر (اختياري — للشراء أو القراءة)</label>
                <input className="forum-form-input" style={{ fontSize: "0.8rem", padding: "5px 8px", direction: "ltr" }}
                  placeholder="https://…" value={item.link_url || ""} onChange={e => update(item.id, { link_url: e.target.value })} />
              </div>
            </div>
          )}
            </>
          )}
        </div>
      ))}
      {queue.length > 0 && !allDone && (
        <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
          <button className="forum-reply-submit" onClick={submitAll} disabled={!anyReady}>
            إرسال {queue.filter(i => i.status === "pending").length} {queue.length === 1 ? "ملف" : "ملفات"}
          </button>
          <button onClick={() => setQueue([])} style={{ background: "none", border: "1px solid var(--border)", borderRadius: "var(--r-xs)", padding: "8px 16px", cursor: "pointer", fontSize: "0.82rem", color: "var(--text-3)" }}>
            مسح الكل
          </button>
        </div>
      )}
      {allDone && (
        <div style={{ background: "rgba(30,86,49,0.08)", border: "1px solid rgba(30,86,49,0.25)", borderRadius: "var(--r-xs)", padding: "12px 16px", fontSize: "0.88rem", color: "var(--green)" }}>
          ✓ تم إرسال {queue.length} {queue.length === 1 ? "ملف" : "ملفات"} — ستظهر بعد موافقة الإدارة. شكراً!
        </div>
      )}
    </div>
  );
}

// ── Drop zone ────────────────────────────────────────────────────────────────
function DropZone({ onFiles, defaultTypes }) {
  const [dragging, setDragging] = useState(false);
  const fileRef   = useRef();
  const folderRef = useRef();

  const processFiles = (files, folderName = null) => {
    const list = [...files].filter(f => f && f.name);
    if (!list.length) {
      if (folderName) alert(`لم يتم العثور على ملفات في المجلد "${folderName}"`);
      return;
    }
    const AUDIO_EXTS = [".mp3", ".wav", ".ogg", ".m4a"];
    const isAudioFile = f => AUDIO_EXTS.some(ext => f.name.toLowerCase().endsWith(ext));

    // Folder drop: extract only audio files — ignore cover images, text files, etc.
    if (folderName) {
      const audioList = list.filter(isAudioFile);
      if (audioList.length >= 5) {
        const valid = [], rejected = [];
        audioList.forEach(f => {
          const err = clientValidate(f);
          if (err) rejected.push(`${f.name}: ${err}`);
          else valid.push(f);
        });
        if (rejected.length) alert("الملفات التالية مرفوضة:\n" + rejected.join("\n"));
        if (valid.length) {
          const sorted = [...valid].sort((a, b) => a.name.localeCompare(b.name));
          onFiles([{
            id: uid(), isAlbum: true, folderName,
            files: sorted.map((f, i) => ({ file: f, name: f.name.replace(/\.[^.]+$/, ""), order: i + 1 })),
            type: "music", status: "pending", title_en: folderName,
            title_ar: "", author_ar: "", description_ar: "", category: "",
          }]);
          return;
        }
      }
      // Not enough audio files — need at least 5 for an album
    }

    // No folder: validate all, then detect album if all are audio (2+)
    const valid = [], rejected = [];
    list.forEach(f => {
      const err = clientValidate(f);
      if (err) rejected.push(`${f.name}: ${err}`);
      else valid.push(f);
    });
    if (rejected.length) alert("الملفات التالية مرفوضة:\n" + rejected.join("\n"));
    if (!valid.length) return;
    const allAudio = valid.every(isAudioFile);
    if (allAudio && valid.length >= 5) {
      const sorted = [...valid].sort((a, b) => a.name.localeCompare(b.name));
      onFiles([{
        id: uid(), isAlbum: true, folderName: null,
        files: sorted.map((f, i) => ({ file: f, name: f.name.replace(/\.[^.]+$/, ""), order: i + 1 })),
        type: "music", status: "pending",
        title_en: sorted[0].name.replace(/\.[^.]+$/, ""),
        title_ar: "", author_ar: "", description_ar: "", category: "",
      }]);
    } else {
      onFiles(valid.map(f => ({
        id: uid(), file: f, type: guessType(f), status: "pending",
        title_en: f.name.replace(/\.[^.]+$/, ""), title_ar: "", author_ar: "", author_en: "", description_ar: "", category: "", link_url: "", coverUrl: "",
      })));
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault(); setDragging(false);
    const items = [...(e.dataTransfer.items || [])];
    const allFiles = [];
    let folderName = null;
    for (const item of items) {
      if (item.kind !== "file") continue;
      const entry = item.webkitGetAsEntry?.();
      if (entry?.isDirectory) {
        const sub = await readDirectoryFiles(entry);
        allFiles.push(...sub);
        if (!folderName) folderName = entry.name;
      } else {
        const f = item.getAsFile();
        if (f) allFiles.push(f);
      }
    }
    processFiles(allFiles, folderName);
  };

  return (
    <div>
      <div
        onDragEnter={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={e => { e.preventDefault(); setDragging(false); }}
        onDragOver={e => e.preventDefault()}
        onDrop={handleDrop}
        style={{
          border: `2px dashed ${dragging ? "var(--green)" : "var(--border)"}`,
          borderRadius: "var(--r-xs)", padding: "28px 20px", textAlign: "center",
          background: dragging ? "rgba(30,86,49,0.06)" : "var(--bg-2)", cursor: "pointer", transition: "all 0.15s",
        }}
      >
        <div style={{ fontSize: "2rem", marginBottom: 8 }}>{dragging ? "📂" : "⬆"}</div>
        <div style={{ fontSize: "0.9rem", fontWeight: 700, marginBottom: 10 }}>
          {dragging ? "أفلت هنا" : "اسحب وأفلت"}
        </div>
        {/* Three drop modes */}
        <div style={{ display: "flex", justifyContent: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
          {[
            { icon: "📚", label: "كتاب واحد", sub: "ملف واحد" },
            { icon: "🗂", label: "مجلد كتب", sub: "مجلد كامل" },
            { icon: "📦", label: "مجموعة كتب", sub: "عدة ملفات" },
          ].map(m => (
            <div key={m.label} style={{
              background: "var(--bg-3)", borderRadius: 8, padding: "8px 14px",
              minWidth: 90, border: "1px solid var(--border)",
            }}>
              <div style={{ fontSize: "1.2rem" }}>{m.icon}</div>
              <div style={{ fontSize: "0.75rem", fontWeight: 600, marginTop: 2 }}>{m.label}</div>
              <div style={{ fontSize: "0.65rem", color: "var(--text-3)" }}>{m.sub}</div>
            </div>
          ))}
        </div>
        <div style={{ fontSize: "0.7rem", color: "var(--text-3)", marginBottom: 14 }}>
          PDF · EPUB · DOC · MP3 · MP4 · صور &nbsp;·&nbsp; حد 3 ملفات/ساعة
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
          <button type="button" className="forum-chip active" onClick={() => fileRef.current.click()}>📚 كتاب / مجموعة</button>
          <button type="button" className="forum-chip active" onClick={() => folderRef.current.click()}>🗂 مجلد كتب</button>
        </div>
      </div>
      <input ref={fileRef}   type="file" multiple accept={ACCEPT} style={{ display: "none" }} onChange={e => { processFiles(e.target.files); e.target.value = ""; }} />
      <input ref={folderRef} type="file" multiple accept={ACCEPT} webkitdirectory="" style={{ display: "none" }} onChange={e => { const fn = e.target.files[0]?.webkitRelativePath?.split("/")?.[0] || null; processFiles(e.target.files, fn); e.target.value = ""; }} />
    </div>
  );
}

// ── Album player ─────────────────────────────────────────────────────────────
function AlbumPlayer({ tracks }) {
  const [idx, setIdx] = useState(0);
  const audioRef = useRef(null);

  const play = (i) => {
    setIdx(i);
    if (audioRef.current) {
      audioRef.current.src = tracks[i].url;
      audioRef.current.play();
    }
  };

  return (
    <div style={{ marginTop: 10, borderTop: "1px solid var(--border)", paddingTop: 10 }}>
      <audio
        ref={audioRef}
        src={tracks[idx]?.url}
        controls
        autoPlay
        onEnded={() => { if (idx < tracks.length - 1) play(idx + 1); }}
        style={{ width: "100%", height: 36, marginBottom: 8 }}
      />
      <ol style={{ margin: 0, paddingRight: 20, paddingLeft: 0, listStyle: "decimal" }}>
        {tracks.map((t, i) => (
          <li
            key={i}
            onClick={() => play(i)}
            style={{
              cursor: "pointer", padding: "5px 4px", fontSize: "0.78rem",
              fontWeight: i === idx ? 700 : 400,
              color: i === idx ? "var(--red)" : "var(--text-2)",
              borderBottom: "1px solid var(--border)",
              display: "flex", alignItems: "center", gap: 6,
            }}
          >
            <span style={{ minWidth: 16, opacity: 0.5, fontSize: "0.7rem" }}>{i === idx ? "▶" : i + 1}</span>
            <span style={{ flex: 1 }}>{t.name}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function BookCard({ item }) {
  const [showPlayer, setShowPlayer] = useState(false);
  const isAlbum = item.tracks?.length > 0;
  const isAudio = ["audio","podcast","music","audiobook"].includes(item.type);

  return (
    <div className="book-card">
      {item.coverUrl
        ? <img className="book-cover" src={item.coverUrl} alt={item.title_ar || item.title_en} />
        : <div className="book-cover-placeholder">{TYPE_ICONS[item.type] || "📚"}</div>
      }
      <div className="book-info">
        {(item.link_url || item.fileUrl)
          ? <a href={item.link_url || item.fileUrl} target="_blank" rel="noopener noreferrer" className="book-title book-title-link">{item.title_ar || item.title_en}</a>
          : <div className="book-title">{item.title_ar || item.title_en}</div>
        }
        {(item.author_ar || item.author_en) && <div className="book-author">{item.author_ar || item.author_en}</div>}
        {item.category && (
          <div style={{ fontSize: "0.7rem", color: "var(--text-3)", background: "var(--bg-3)", borderRadius: 10, padding: "1px 8px", display: "inline-block", marginBottom: 6 }}>{item.category}</div>
        )}
        {item.description_ar && (
          <div style={{ fontSize: "0.75rem", color: "var(--text-3)", lineHeight: 1.5, marginBottom: 8, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            {item.description_ar}
          </div>
        )}
        {isAlbum && isAudio ? (
          <div>
            {item.canRead !== false && (
              <button className="book-btn" onClick={() => setShowPlayer(s => !s)}>
                {showPlayer ? "⏹ إغلاق" : "▶ استماع"}
                <span style={{ fontSize: "0.68rem", opacity: 0.7, marginRight: 4 }}>({item.tracks.length} مقطع)</span>
              </button>
            )}
            {item.canDownload !== false && !item.isPermanent && (
              <button className="book-btn" style={{ background: "var(--bg-2)", color: "var(--text-2)", marginTop: 4 }}
                onClick={() => downloadFile(item.tracks[0].url, item.title_en || item.title_ar)}>📥 تنزيل</button>
            )}
            {item.link_url && (
              <a href={item.link_url} target="_blank" rel="noopener noreferrer" className="book-btn"
                style={{ background: "var(--bg-2)", color: "var(--text-2)", marginTop: 4, display: "block", textAlign: "center" }}>
                🔗 زيارة المصدر
              </a>
            )}
            {showPlayer && <AlbumPlayer tracks={item.tracks} />}
          </div>
        ) : item.fileUrl ? (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {item.canRead !== false && (
              <a href={item.fileUrl} target="_blank" rel="noopener noreferrer" className="book-btn">{VIEW_LABEL[item.type] || "📖 قراءة"}</a>
            )}
            {item.canDownload !== false && !item.isPermanent && (
              <button className="book-btn" style={{ background: "var(--bg-2)", color: "var(--text-2)" }}
                onClick={() => downloadFile(item.fileUrl, item.title_en || item.title_ar)}>📥 تنزيل</button>
            )}
            {item.link_url && (
              <a href={item.link_url} target="_blank" rel="noopener noreferrer" className="book-btn"
                style={{ background: "var(--bg-2)", color: "var(--text-2)" }}>🔗 زيارة المصدر</a>
            )}
            {item.canRead === false && item.canDownload === false && !item.link_url && (
              <span style={{ fontSize: "0.78rem", color: "var(--text-3)" }}>غير متاح للعموم</span>
            )}
          </div>
        ) : item.link_url ? (
          <a href={item.link_url} target="_blank" rel="noopener noreferrer" className="book-btn">🔗 زيارة المصدر</a>
        ) : (
          <button className="book-btn" disabled style={{ opacity: 0.5 }}>قريباً</button>
        )}
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────
function CollectionPage({ collection }) {
  const cfg = COLLECTIONS[collection] || COLLECTIONS.bookshelf;
  const { isLoggedIn, token } = useAuth();
  const { isMember } = useForum();

  const [items, setItems]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [typeFilter, setTypeFilter]         = useState("all");
  const [groupByAuthor, setGroupByAuthor]   = useState(false);
  const [showSubmit, setShowSubmit] = useState(false);
  const [queue, setQueue]           = useState([]);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API_BASE}/api/forum/bookshelf?collection=${collection}`)
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [collection]);

  const availableCategories = [...new Set(items.map(i => i.category).filter(Boolean))];
  const byCat  = categoryFilter === "all" ? items : items.filter(i => i.category === categoryFilter);
  const visible = typeFilter === "all" ? byCat : byCat.filter(i => i.type === typeFilter);

  // Author grouping: group by author_ar or author_en; "غير معروف" for unlabelled
  const authorGroups = (() => {
    if (!groupByAuthor) return null;
    const map = {};
    visible.forEach(item => {
      const key = item.author_ar || item.author_en || "غير معروف";
      if (!map[key]) map[key] = [];
      map[key].push(item);
    });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b, "ar"));
  })();

  return (
    <div className="forum-wrap">
      <div className="forum-breadcrumb">
        <Link to="/Forum">المنتدى</Link>
        <span className="forum-breadcrumb-sep">›</span>
        <span>{cfg.breadcrumb}</span>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24, flexWrap: "wrap" }}>
        <div style={{ fontSize: "2.5rem" }}>{cfg.icon}</div>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: "1.4rem", fontWeight: 800, margin: 0 }}>{cfg.title_ar}</h1>
          <div style={{ fontSize: "0.82rem", color: "var(--text-3)" }}>{cfg.title_en} · {cfg.subtitle}</div>
        </div>
        {isMember && (
          <button className="forum-reply-submit" onClick={() => setShowSubmit(s => !s)}>
            {showSubmit ? "إلغاء" : cfg.submitLabel}
          </button>
        )}
      </div>

      {showSubmit && isMember && (
        <div style={{ marginBottom: 28 }}>
          <h3 style={{ fontWeight: 700, marginBottom: 12, fontSize: "1rem" }}>{cfg.submitTitle}</h3>
          <DropZone onFiles={f => { setQueue(q => [...q, ...f]); }} defaultTypes={cfg.types} />
          <UploadQueue queue={queue} setQueue={setQueue} token={token} collection={collection} defaultTypes={cfg.types} />
        </div>
      )}

      {/* Category filter chips */}
      {availableCategories.length > 0 && (
        <div className="forum-chips" style={{ marginBottom: 8 }}>
          <button className={`forum-chip ${categoryFilter === "all" ? "active" : ""}`} onClick={() => setCategoryFilter("all")}>كل التصنيفات</button>
          {availableCategories.map(cat => (
            <button key={cat} className={`forum-chip ${categoryFilter === cat ? "active" : ""}`} onClick={() => setCategoryFilter(cat)}>
              {cat}
            </button>
          ))}
        </div>
      )}

      {/* Type filter + group toggle */}
      <div className="forum-chips" style={{ justifyContent: "space-between", flexWrap: "wrap", gap: 6 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          <button className={`forum-chip ${typeFilter === "all" ? "active" : ""}`} onClick={() => setTypeFilter("all")}>الكل</button>
          {cfg.types.map(t => (
            <button key={t} className={`forum-chip ${typeFilter === t ? "active" : ""}`} onClick={() => setTypeFilter(t)}>
              {TYPE_ICONS[t]} {TYPE_LABELS[t]}
            </button>
          ))}
        </div>
        <button
          className={`forum-chip ${groupByAuthor ? "active" : ""}`}
          onClick={() => setGroupByAuthor(v => !v)}
          title="تجميع حسب المؤلف"
        >
          👤 حسب المؤلف
        </button>
      </div>

      {/* Grid or Author groups */}
      {loading ? (
        <div className="forum-loading">جارٍ التحميل…</div>
      ) : visible.length === 0 ? (
        <div className="forum-empty">
          لا توجد ملفات في هذا القسم بعد.
          {isMember && <div style={{ marginTop: 12 }}><button className="forum-reply-submit" onClick={() => setShowSubmit(true)}>{cfg.submitLabel}</button></div>}
          {!isMember && !isLoggedIn && <div style={{ marginTop: 12 }}><Link to="/login" style={{ color: "var(--red)" }}>سجّل الدخول</Link> للإضافة.</div>}
          {isLoggedIn && !isMember && <div style={{ marginTop: 12 }}><Link to="/Forum/register" style={{ color: "var(--green)" }}>انضم للمنتدى</Link> للإضافة.</div>}
        </div>
      ) : authorGroups ? (
        <div>
          {authorGroups.map(([author, books]) => (
            <div key={author} style={{ marginBottom: 36 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, paddingBottom: 8, borderBottom: "1px solid var(--border)" }}>
                <span style={{ fontSize: "1.1rem" }}>📚</span>
                <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 700 }}>{author}</h3>
                <span style={{ fontSize: "0.72rem", color: "var(--text-3)", background: "var(--bg-3)", borderRadius: 10, padding: "1px 8px" }}>
                  {books.length} {books.length === 1 ? "كتاب" : "كتب"}
                </span>
              </div>
              <div className="bookshelf-grid">
                {books.map(item => <BookCard key={item._id} item={item} />)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bookshelf-grid">
          {visible.map(item => (
            <BookCard key={item._id} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function ForumBookshelf({ collection = "bookshelf" }) {
  return (
    <ForumProvider>
      <CollectionPage collection={collection} />
    </ForumProvider>
  );
}

export { COLLECTIONS };
